import { Component } from '@wonderlandengine/api';

/**
 * Progression System for Arena Wars
 * Handles Rent-to-Own War Coin economy, Dual Loadouts, and Prestige
 */
export class ProgressionSystem {
    static MAX_LEVEL = 25;
    static MAX_PRESTIGE = 3;

    // Disabled Level Gating: Base unlock levels remain at 999.
    static BASE_UNLOCK_LEVELS = {
        'akimbo_crosshair': 999,
        'rifle_crosshair': 999,
        'shotgun_crosshair': 999,
        'akimbo_laser': 999,
        'rifle_laser': 999,
        'shotgun_laser': 999,
        'rifle_permanent': 999,
        'shotgun_permanent': 999,
        'sniper_permanent': 999,
        'sniper_crosshair': 999,
        'sniper_laser': 999,
        'laser_blue': 999,
        'laser_purple': 999,
        'laser_gold': 999,
    };

    // Explicit order for unlocks (for display and debug)
    static ORDERED_UNLOCK_KEYS = [
        'akimbo_crosshair',
        'rifle_crosshair',
        'shotgun_crosshair',
        'akimbo_laser',
        'rifle_laser',
        'shotgun_laser',
        'rifle_permanent',
        'shotgun_permanent',
        'sniper_permanent',
        'sniper_crosshair',
        'sniper_laser',
        'laser_blue',
        'laser_purple',
        'laser_gold',
    ];

    // ── WAR COINS ECONOMY (RENT-TO-OWN) ───────────────────────────────────────
    static COIN_PRICES = {
        weapons: {
            'rifle':   { rental: 75,  permanent: 1200 },
            'shotgun': { rental: 75,  permanent: 1200 },
            'sniper':  { rental: 150, permanent: 2500 }
        },
        attachments: {
            'akimbo_crosshair':  150,
            'rifle_crosshair':   200,
            'shotgun_crosshair': 200,
            'sniper_crosshair':  350,
            'akimbo_laser':      250,
            'rifle_laser':       300,
            'shotgun_laser':     300,
            'sniper_laser':      500,
            'laser_blue':        400,
            'laser_purple':      600,
            'laser_gold':        999,
        }
    };

    // Cosmetic Exclusives based on Prestige Milestone
    static PRESTIGE_REWARDS = {
        1: ['laser_blue'],    // Veteran
        2: ['laser_purple'],  // Elite
        3: ['laser_gold']     // Master
    };

    // Cached pre-allocated Float32Arrays for colors
    static LASER_COLORS = [
        new Float32Array([0.0, 1.0, 1.0, 1.0]), // 0: Rookie (Cyan)
        new Float32Array([0.0, 0.8, 1.0, 1.0]), // 1: Veteran (Blue)
        new Float32Array([0.8, 0.0, 1.0, 1.0]), // 2: Elite (Purple)
        new Float32Array([1.0, 0.9, 0.0, 1.0])  // 3: Master (Gold)
    ];
    static FALLBACK_LASER_COLOR = new Float32Array([0.0, 1.0, 1.0, 1.0]);
    static EMPTY_ARRAY = [];

    /**
     * Get War Coin earn multiplier based on prestige tier
     * @param {number} prestigeTier 
     * @returns {number} Multiplier (1.0 to 1.5)
     */
    static getPrestigeCoinMultiplier(prestigeTier) {
        switch (Number(prestigeTier)) {
            case 1: return 1.10; // +10%
            case 2: return 1.25; // +25%
            case 3: return 1.50; // +50%
            default: return 1.0;
        }
    }

    /**
     * Rewrite the isUnlocked function to use the new economy structure
     * @param {string} itemId - e.g., 'rifle', 'laser_blue', 'rifle_crosshair'
     * @param {string} itemType - 'weapon' or 'attachment'
     * @param {Object} economyData - sd.economy
     * @param {string} [parentWeaponId] - Required for attachments to verify weapon license
     */
    static isUnlocked(itemId, itemType, economyData, parentWeaponId = null) {
        if (!economyData) return false;

        // 1. Default starting items
        if (itemId === 'default' || itemId === 'akimbo' || itemId === 'none') return true;

        const prestige = economyData.prestigeTier || 0;
        const licenses = economyData.licenses || { weapons: {}, attachments: [] };

        // Check Prestige Rewards (Cosmetics)
        for (let p = 1; p <= prestige; p++) {
            if (ProgressionSystem.PRESTIGE_REWARDS[p] && ProgressionSystem.PRESTIGE_REWARDS[p].includes(itemId)) {
                return true;
            }
        }

        if (itemType === 'weapon') {
            const wLicense = licenses.weapons[itemId];
            if (!wLicense) return false;
            return wLicense.isPermanent || wLicense.matchesRemaining > 0;
        }

        if (itemType === 'attachment') {
            const hasAttachment = licenses.attachments.includes(itemId);
            if (!hasAttachment) return false;

            // If it's a weapon-specific attachment, check if we hold the weapon license
            if (parentWeaponId && parentWeaponId !== 'default' && parentWeaponId !== 'akimbo') {
                const pLicense = licenses.weapons[parentWeaponId];
                if (!pLicense || (!pLicense.isPermanent && pLicense.matchesRemaining <= 0)) {
                    return false; // Parent weapon is not licensed
                }
            }
            return true;
        }

        return false;
    }

    /**
     * Validates the full loadout and strips unowned/expired items
     * @param {Object} loadout - sd.loadout
     * @param {Object} economy - sd.economy
     * @returns {Object} Validated loadout
     */
    static validateLoadout(loadout, economy) {
        const validLoadout = {
            primary: { id: 'default', attachments: { laser: 'none', crosshair: 'none' } },
            secondary: { id: 'default', attachments: { laser: 'none', crosshair: 'none' } }
        };

        if (!loadout || !economy) return validLoadout;

        // Validate Primary
        if (loadout.primary && ProgressionSystem.isUnlocked(loadout.primary.id, 'weapon', economy)) {
            validLoadout.primary.id = loadout.primary.id;
            // Validate primary attachments
            if (loadout.primary.attachments) {
                if (loadout.primary.attachments.laser && ProgressionSystem.isUnlocked(loadout.primary.attachments.laser, 'attachment', economy, loadout.primary.id)) {
                    validLoadout.primary.attachments.laser = loadout.primary.attachments.laser;
                }
                if (loadout.primary.attachments.crosshair && ProgressionSystem.isUnlocked(loadout.primary.attachments.crosshair, 'attachment', economy, loadout.primary.id)) {
                    validLoadout.primary.attachments.crosshair = loadout.primary.attachments.crosshair;
                }
            }
        }

        // Validate Secondary
        if (loadout.secondary && ProgressionSystem.isUnlocked(loadout.secondary.id, 'weapon', economy)) {
            validLoadout.secondary.id = loadout.secondary.id;
            // Validate secondary attachments
            if (loadout.secondary.attachments) {
                if (loadout.secondary.attachments.laser && ProgressionSystem.isUnlocked(loadout.secondary.attachments.laser, 'attachment', economy, loadout.secondary.id)) {
                    validLoadout.secondary.attachments.laser = loadout.secondary.attachments.laser;
                }
                if (loadout.secondary.attachments.crosshair && ProgressionSystem.isUnlocked(loadout.secondary.attachments.crosshair, 'attachment', economy, loadout.secondary.id)) {
                    validLoadout.secondary.attachments.crosshair = loadout.secondary.attachments.crosshair;
                }
            }
        }

        return validLoadout;
    }

    /**
     * Decrements the rental counters for equipped weapons after a match
     * @param {Object} profile - Full sd object containing loadout and economy
     */
    static decrementMatchLicenses(profile) {
        if (!profile || !profile.loadout || !profile.economy || !profile.economy.licenses || !profile.economy.licenses.weapons) return;
        
        const weapons = profile.economy.licenses.weapons;
        
        const pId = profile.loadout.primary?.id;
        const sId = profile.loadout.secondary?.id;
        
        if (pId && weapons[pId] && !weapons[pId].isPermanent && weapons[pId].matchesRemaining > 0) {
            weapons[pId].matchesRemaining -= 1;
        }
        
        // Ensure we don't double decrement if they equipped the same weapon twice (not normally allowed but just in case)
        if (sId && sId !== pId && weapons[sId] && !weapons[sId].isPermanent && weapons[sId].matchesRemaining > 0) {
            weapons[sId].matchesRemaining -= 1;
        }
    }

    static getLaserColor(prestige) {
        return ProgressionSystem.LASER_COLORS[prestige] || ProgressionSystem.FALLBACK_LASER_COLOR;
    }

    // ── LEGACY BACKWARDS COMPATIBILITY ───────────────────────────────────────
    // Retaining these to prevent immediate crashes in untouched systems (e.g. leaderboards).

    static getEffectiveLevel(level, prestige) {
        return level + (prestige * ProgressionSystem.MAX_LEVEL);
    }
    
    static calculatePrestigeAndLevel(effectiveLevel, out = null) {
        const result = out || { prestige: 0, level: 0 };
        const prestige = Math.floor((effectiveLevel - 1) / ProgressionSystem.MAX_LEVEL);
        if (prestige >= ProgressionSystem.MAX_PRESTIGE) {
            const levelInMaxPrestige = effectiveLevel - (ProgressionSystem.MAX_PRESTIGE * ProgressionSystem.MAX_LEVEL);
            result.prestige = ProgressionSystem.MAX_PRESTIGE;
            result.level = Math.max(1, levelInMaxPrestige);
            return result;
        }
        const level = ((effectiveLevel - 1) % ProgressionSystem.MAX_LEVEL) + 1;
        result.prestige = prestige;
        result.level = level;
        return result;
    }

    static getPrestigeName(prestige) {
        const names = {
            0: 'Rookie',
            1: 'Veteran',
            2: 'Elite',
            3: 'Master'
        };
        return names[prestige] || `Prestige ${prestige}`;
    }

    static getUnlockDescription(unlockKey) {
        const descriptions = {
            'akimbo_crosshair': 'Akimbo Crosshair',
            'rifle_crosshair': 'Rifle Crosshair',
            'shotgun_crosshair': 'Shotgun Crosshair',
            'sniper_crosshair': 'Sniper Crosshair',
            'akimbo_laser': 'Akimbo Laser',
            'rifle_laser': 'Rifle Laser',
            'shotgun_laser': 'Shotgun Laser',
            'sniper_laser': 'Sniper Laser',
            'rifle_permanent': 'Rifle Permanent',
            'shotgun_permanent': 'Shotgun Permanent',
            'sniper_permanent': 'Sniper Permanent',
            'laser_blue': 'Blue Laser',
            'laser_purple': 'Purple Laser',
            'laser_gold': 'Gold Laser',
            'prestige': 'Prestige Available',
            'endless': 'Legend Status'
        };
        return descriptions[unlockKey] || unlockKey;
    }

    static getPrestigeRewards(prestige) {
        return ProgressionSystem.PRESTIGE_REWARDS[prestige] || ProgressionSystem.EMPTY_ARRAY;
    }

    static getUnlocksAtLevel(level, prestige = 0) {
        return [];
    }

    static getNextUnlock(currentLevel, prestige = 0) {
        if (currentLevel >= ProgressionSystem.MAX_LEVEL && prestige < ProgressionSystem.MAX_PRESTIGE) {
            return {
                key: 'prestige',
                level: ProgressionSystem.MAX_LEVEL + 1,
                description: `Prestige ${prestige + 1} Available`
            };
        }
        if (prestige >= ProgressionSystem.MAX_PRESTIGE) {
            return {
                key: 'endless',
                level: currentLevel + 1,
                description: `Legend Status - Endless Progression`
            };
        }
        return null;
    }
}